import type React from 'react';

import isEqual from 'lodash/isEqual';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { ContentCard, type ContentCardProps } from './content-card';
import { type ContentModalProps, GenericContentModal } from './generic-content-modal';
import { SectionHeader, type SectionHeaderProps } from './section-header';

export interface EditModalValue {
    [key: string]: string;
}

export interface GenericContentCardsContent {
    buttonText?: string;
    description: string;
    displayOrder?: string;
    headingText: string;
    id?: string;
    link?: string;
    type?: string;
}

export interface EditModalFields {
    contentFieldMapper: string;
    fieldName: string;
    label?: string;
    maxLength?: number;
    placeholder?: string;
    required: boolean;
    showCharCount?: boolean;
    type?: 'TextInput' | 'TextArea';
}

export type GenericContentProps<T = EditModalValue> = {
    cardProps?: Partial<ContentCardProps>;
    confirmDeleteModal?: boolean;
    contents?: GenericContentCardsContent[];
    deleteCallBack?: (id: string) => void;
    headerProps?: Partial<SectionHeaderProps>;
    maxInstance?: number;
    minInstance?: number;
    modalProps?: Partial<ContentModalProps>;
    saveCallback?: (value: T | EditModalValue) => void;
    sortingEnabled?: boolean;
};

const styles = StyleSheet.create({
    cardsContainer: {
        marginTop: 24,
    },
});

const initializeEditModalValue = (fields?: EditModalFields[]): EditModalValue =>
    fields?.reduce((acc, field) => ({ ...acc, [field.fieldName]: '' }), {}) || {};

export const GenericContentManager: FC<{ genericContentProps: GenericContentProps }> = ({ genericContentProps }) => {
    const {
        cardProps = {},
        confirmDeleteModal = false,
        contents = [],
        deleteCallBack,
        headerProps = {},
        maxInstance = 1,
        modalProps = {},
        saveCallback,
        sortingEnabled = false,
    } = genericContentProps;

    const [copied, setCopied] = useState(false);
    const [componentTypeId] = useState(() => Math.floor(Math.random() * 1000));
    const [draggingElementId, setDraggingElementId] = useState<string | null>(null);

    const editModalRefObject = useMemo(
        () => initializeEditModalValue(modalProps?.formFields),
        [modalProps?.formFields]
    );

    const [editModalEnteredValue, setEditModalEnteredValue] = useState<EditModalValue>(editModalRefObject);
    const [initialEditModalValue, setInitialEditModalValue] = useState<EditModalValue>(editModalRefObject);
    const [instanceSize, setInstanceSize] = useState<number>(contents.length);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [stateContent, setStateContent] = useState<GenericContentCardsContent[]>(contents);

    const sortedContent = useMemo(
        () => [...stateContent].sort((a, b) => Number(a.displayOrder || 0) - Number(b.displayOrder || 0)),
        [stateContent]
    );

    const editModalRequiredFields = useMemo(
        () => modalProps?.formFields?.filter((field) => field.required).map((field) => field.fieldName) || [],
        [modalProps?.formFields]
    );

    useEffect(() => {
        setStateContent(contents);
        setInstanceSize(contents.length);
    }, [contents]);

    const handleHideShowModal = () => {
        setShowModal(false);
        setCopied(false);
    };

    const handleSave = () => {
        saveCallback?.(editModalEnteredValue);
        setInitialEditModalValue(editModalEnteredValue);
        setShowModal(false);
        setCopied(false);
    };

    /** Clears the source record after the browser drag lifecycle ends. */
    const handleDragEnd = useCallback(() => {
        setDraggingElementId(null);
    }, []);

    const enableEditModalSubmitButton = useMemo(() => {
        const hasChanges = !isEqual(editModalEnteredValue, initialEditModalValue);
        return !hasChanges || !editModalRequiredFields.every((field) => editModalEnteredValue[field]?.trim());
    }, [editModalEnteredValue, initialEditModalValue, editModalRequiredFields]);

    const handleDragStart = (event: React.DragEvent, contentId?: string) => {
        if (!sortingEnabled || !contentId) {
            return;
        }
        event.dataTransfer.setData('text/plain', componentTypeId.toString());
        setDraggingElementId(contentId);
    };

    const handleDrop = (event: React.DragEvent, targetContentId?: string) => {
        if (!sortingEnabled || draggingElementId === null || !targetContentId) {
            handleDragEnd();
            return;
        }

        if (event.dataTransfer.getData('text/plain') !== String(componentTypeId)) {
            handleDragEnd();
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const draggedContentId = draggingElementId;
        handleDragEnd();

        const draggedContent = stateContent.find((content) => content.id === draggedContentId);
        const targetContent = stateContent.find((content) => content.id === targetContentId);
        const targetDisplayOrder = targetContent?.displayOrder;
        const numericTargetDisplayOrder = Number(targetDisplayOrder);

        if (
            !draggedContent ||
            !targetContent ||
            draggedContent.id === targetContent.id ||
            !targetDisplayOrder ||
            !Number.isInteger(numericTargetDisplayOrder) ||
            numericTargetDisplayOrder <= 0
        ) {
            return;
        }

        saveCallback?.({ ...draggedContent, displayOrder: targetDisplayOrder });
    };

    const openModal = (content: GenericContentCardsContent) => {
        setInitialEditModalValue({ ...content });
        setEditModalEnteredValue({ ...content });
        setShowModal(true);
    };

    return (
        <>
            <SectionHeader
                title={headerProps.title || 'Messages'}
                description={`${instanceSize}/${maxInstance}`}
                onPress={() => openModal({ headingText: '', description: '' })}
                isDisabled={instanceSize >= maxInstance}
                buttonLabel={headerProps.buttonLabel}
                hideButton={headerProps.hideButton}
            />
            <View>
                {sortedContent.map((content, index) => (
                    <div
                        draggable={sortingEnabled && Boolean(content.id)}
                        key={content.id || index}
                        onDragEnd={handleDragEnd}
                        onDragOver={(event) => event.preventDefault()}
                        onDragStart={(event) => handleDragStart(event, content.id)}
                        onDrop={(event) => handleDrop(event, content.id)}
                    >
                        <div style={styles.cardsContainer}>
                            <ContentCard
                                content={content}
                                onDelete={deleteCallBack}
                                formFields={modalProps?.formFields || []}
                                toggleModalVisibility={handleHideShowModal}
                                hideCopyButton={!cardProps.hideCopyButton && instanceSize < maxInstance}
                                index={index}
                                setFormData={(value) => {
                                    setEditModalEnteredValue(value);
                                    setInitialEditModalValue(value);
                                }}
                                setShowModal={setShowModal}
                                contentList={stateContent}
                                title={`${index + 1} - ${cardProps.title}`}
                                confirmDeleteModal={confirmDeleteModal}
                                setCopied={setCopied}
                            />
                        </div>
                    </div>
                ))}
            </View>
            <GenericContentModal
                disabled={enableEditModalSubmitButton && !copied}
                formData={editModalEnteredValue}
                formFields={modalProps?.formFields || []}
                handleHideShowModal={handleHideShowModal}
                handleSubmit={handleSave}
                setFormData={setEditModalEnteredValue}
                showModal={showModal}
                submitButtonText={editModalEnteredValue.id ? 'Save & Publish' : modalProps.submitButtonText || 'Submit'}
                title={`${editModalEnteredValue.id ? editModalEnteredValue.index : contents.length + 1} - ${
                    modalProps.title || 'Edit Content'
                }`}
            />
        </>
    );
};
